import { View, Text, styled, useTheme } from 'tamagui'
import { X, ArrowLeft } from '@tamagui/lucide-icons'
import { useState } from 'react'
import { useAppStore, useServerStore } from '@/store'

const Backdrop = styled(View, {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  // @ts-expect-error -- web backdrop-filter
  style: { backdropFilter: 'blur(8px)' },
  zIndex: 2000,
  alignItems: 'center',
  justifyContent: 'center',
})

const Modal = styled(View, {
  width: 440,
  backgroundColor: '$surfaceRaised',
  borderRadius: '$borderRadiusXl',
  borderWidth: 1,
  borderColor: '$borderDefault',
  overflow: 'hidden',
})

const ModalHeader = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingHorizontal: 20,
  paddingVertical: 16,
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
})

const ModalTitle = styled(Text, {
  fontFamily: '$fontHeading',
  fontWeight: '600',
  fontSize: 18,
  color: '$surfaceForeground',
})

const ModalBody = styled(View, {
  padding: 20,
})

const CloseButton = styled(View, {
  width: 28,
  height: 28,
  borderRadius: '$borderRadiusMd',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
})

const BackButton = styled(View, {
  width: 28,
  height: 28,
  borderRadius: '$borderRadiusMd',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
})

// Step 1: Template selection
const TemplateGrid = styled(View, {
  gap: 8,
})

const TemplateItem = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  height: 48,
  paddingHorizontal: 16,
  borderRadius: '$borderRadiusLg',
  borderWidth: 1,
  borderColor: '$borderDefault',
  cursor: 'pointer',
  gap: 12,
  hoverStyle: {
    backgroundColor: '$sidebarHover',
    borderColor: '$accent',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease, border-color 150ms ease' },
})

const TemplateIcon = styled(Text, {
  fontSize: 20,
  width: 28,
  textAlign: 'center',
})

const TemplateName = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 14,
  fontWeight: '500',
  color: '$surfaceForeground',
})

// Step 2: Form
const FormGroup = styled(View, {
  gap: 6,
  marginBottom: 16,
})

const Label = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 12,
  fontWeight: '600',
  color: '$color8',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
})

const ColorPicker = styled(View, {
  flexDirection: 'row',
  gap: 8,
  flexWrap: 'wrap',
})

const ColorDot = styled(View, {
  width: 32,
  height: 32,
  borderRadius: '$borderRadius2xl',
  cursor: 'pointer',
  alignItems: 'center',
  justifyContent: 'center',
  // @ts-expect-error -- web transition
  style: { transition: 'transform 100ms ease' },
})

const ToggleRow = styled(View, {
  flexDirection: 'row',
  gap: 8,
})

const ToggleButton = styled(View, {
  flex: 1,
  height: 36,
  borderRadius: '$borderRadiusMd',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  borderWidth: 1,
  borderColor: '$borderDefault',
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease, border-color 150ms ease' },
  variants: {
    active: {
      true: {
        backgroundColor: '$accentLighter',
        borderColor: '$accent',
      },
    },
  } as const,
})

const CreateBtn = styled(View, {
  height: 40,
  borderRadius: '$borderRadiusLg',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '$accent',
  cursor: 'pointer',
  marginTop: 8,
  hoverStyle: {
    opacity: 0.9,
  },
})

interface Template {
  id: string
  name: string
  icon: string
  defaultName: string
  categories: { name: string; channels: { name: string; type: 'text' | 'voice' }[] }[]
}

const templates: Template[] = [
  {
    id: 'scratch',
    name: 'From Scratch',
    icon: '✨',
    defaultName: 'My Server',
    categories: [
      { name: 'Text Channels', channels: [{ name: 'general', type: 'text' }] },
      { name: 'Voice Channels', channels: [{ name: 'General', type: 'voice' }] },
    ],
  },
  {
    id: 'gaming',
    name: 'Gaming',
    icon: '🎮',
    defaultName: 'Gaming Hub',
    categories: [
      { name: 'Info', channels: [{ name: 'rules', type: 'text' }, { name: 'announcements', type: 'text' }] },
      { name: 'Chat', channels: [{ name: 'general', type: 'text' }, { name: 'looking-for-group', type: 'text' }] },
      { name: 'Voice', channels: [{ name: 'Game Room 1', type: 'voice' }, { name: 'Game Room 2', type: 'voice' }] },
    ],
  },
  {
    id: 'study',
    name: 'Study Group',
    icon: '📚',
    defaultName: 'Study Group',
    categories: [
      { name: 'General', channels: [{ name: 'general', type: 'text' }, { name: 'resources', type: 'text' }] },
      { name: 'Study', channels: [{ name: 'questions', type: 'text' }, { name: 'Study Room', type: 'voice' }] },
    ],
  },
  {
    id: 'friends',
    name: 'Friends',
    icon: '👥',
    defaultName: 'Friend Group',
    categories: [
      { name: 'Chat', channels: [{ name: 'general', type: 'text' }, { name: 'memes', type: 'text' }] },
      { name: 'Hangout', channels: [{ name: 'Lounge', type: 'voice' }] },
    ],
  },
  {
    id: 'community',
    name: 'Community',
    icon: '🌐',
    defaultName: 'Community',
    categories: [
      { name: 'Information', channels: [{ name: 'welcome', type: 'text' }, { name: 'rules', type: 'text' }, { name: 'announcements', type: 'text' }] },
      { name: 'General', channels: [{ name: 'general', type: 'text' }, { name: 'off-topic', type: 'text' }] },
      { name: 'Voice', channels: [{ name: 'Lounge', type: 'voice' }] },
    ],
  },
]

const colorOptions = [
  '#3ECFCF', '#E8A840', '#CF3E4F', '#A855F7',
  '#3ECF7B', '#3B82F6', '#EC4899', '#8B95A8',
]

export function CreateServerModal() {
  const { closeCreateServerModal, navigateToServer, setActiveChannel } = useAppStore()
  const { addServer } = useServerStore()
  const theme = useTheme()
  const [step, setStep] = useState<1 | 2>(1)
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null)
  const [name, setName] = useState('')
  const [color, setColor] = useState(colorOptions[0])
  const [isPublic, setIsPublic] = useState(true)
  const [description, setDescription] = useState('')

  const handleSelectTemplate = (template: Template) => {
    setSelectedTemplate(template)
    setName(template.defaultName)
    setStep(2)
  }

  const handleCreate = () => {
    if (!selectedTemplate || !name.trim()) return

    const serverId = `s${Date.now()}`
    const categories = selectedTemplate.categories.map((cat, cIdx) => ({
      id: `${serverId}-cat${cIdx}`,
      name: cat.name,
      collapsed: false,
    }))

    let channelIdx = 0
    const channels = selectedTemplate.categories.flatMap((cat, cIdx) =>
      cat.channels.map((ch) => ({
        id: `${serverId}-ch${channelIdx++}`,
        name: ch.name,
        type: ch.type as 'text' | 'voice',
        categoryId: `${serverId}-cat${cIdx}`,
        topic: '',
        unreadCount: 0,
        mentionCount: 0,
      }))
    )

    const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()

    addServer({
      id: serverId,
      name: name.trim(),
      iconText: initials,
      iconColor: color,
      categories,
      channels,
      memberCount: 1,
      unreadCount: 0,
      mentionCount: 0,
    })

    navigateToServer(serverId)
    const firstText = channels.find(c => c.type === 'text')
    if (firstText) setActiveChannel(firstText.id)
    closeCreateServerModal()
  }

  return (
    <Backdrop onPress={closeCreateServerModal}>
      <Modal
        onPress={(e: any) => e.stopPropagation()}
        // @ts-expect-error -- web box-shadow
        style={{ boxShadow: theme.shadowModal?.val || 'none' }}
      >
        <ModalHeader>
          <View flexDirection="row" alignItems="center" gap={8}>
            {step === 2 && (
              <BackButton onPress={() => setStep(1)}>
                <ArrowLeft size={16} color="$sidebarMuted" />
              </BackButton>
            )}
            <ModalTitle>{step === 1 ? 'Create a Server' : 'Customize Your Server'}</ModalTitle>
          </View>
          <CloseButton onPress={closeCreateServerModal}>
            <X size={16} color="$sidebarMuted" />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {step === 1 ? (
            <TemplateGrid>
              {templates.map((t) => (
                <TemplateItem key={t.id} onPress={() => handleSelectTemplate(t)}>
                  <TemplateIcon>{t.icon}</TemplateIcon>
                  <TemplateName>{t.name}</TemplateName>
                </TemplateItem>
              ))}
            </TemplateGrid>
          ) : (
            <View>
              <FormGroup>
                <Label>Server Name</Label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="My Awesome Server"
                  style={{
                    background: 'transparent',
                    border: `1px solid ${theme.borderDefault?.val || '#2A3142'}`,
                    borderRadius: 6,
                    padding: '8px 12px',
                    color: theme.surfaceForeground?.val || '#E2E8F0',
                    fontFamily: '$fontBody',
                    fontSize: 14,
                    outline: 'none',
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                />
              </FormGroup>

              <FormGroup>
                <Label>Icon Color</Label>
                <ColorPicker>
                  {colorOptions.map((c) => (
                    <ColorDot
                      key={c}
                      backgroundColor={c}
                      onPress={() => setColor(c)}
                      borderWidth={color === c ? 2 : 0}
                      borderColor="$surfaceForeground"
                      // @ts-expect-error -- web transform
                      style={color === c ? { transform: 'scale(1.15)' } : undefined}
                    >
                      {color === c && <Text color="$surface" fontSize={14} fontWeight="700">✓</Text>}
                    </ColorDot>
                  ))}
                </ColorPicker>
              </FormGroup>

              <FormGroup>
                <Label>Visibility</Label>
                <ToggleRow>
                  <ToggleButton active={isPublic} onPress={() => setIsPublic(true)}>
                    <Text fontFamily="$fontBody" fontSize={13} color={isPublic ? '$accent' : '$sidebarMuted'}>Public</Text>
                  </ToggleButton>
                  <ToggleButton active={!isPublic} onPress={() => setIsPublic(false)}>
                    <Text fontFamily="$fontBody" fontSize={13} color={!isPublic ? '$accent' : '$sidebarMuted'}>Private</Text>
                  </ToggleButton>
                </ToggleRow>
              </FormGroup>

              <FormGroup>
                <Label>Description (optional)</Label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What's this server about?"
                  rows={2}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${theme.borderDefault?.val || '#2A3142'}`,
                    borderRadius: 6,
                    padding: '8px 12px',
                    color: theme.surfaceForeground?.val || '#E2E8F0',
                    fontFamily: '$fontBody',
                    fontSize: 14,
                    outline: 'none',
                    resize: 'none',
                    width: '100%',
                    boxSizing: 'border-box',
                  }}
                />
              </FormGroup>

              <CreateBtn
                onPress={handleCreate}
                // @ts-expect-error -- web box-shadow
                style={{ boxShadow: theme.shadowButton?.val }}
              >
                <Text fontFamily="$fontBody" fontWeight="600" fontSize={14} color="$surface">
                  Create Server
                </Text>
              </CreateBtn>
            </View>
          )}
        </ModalBody>
      </Modal>
    </Backdrop>
  )
}
